import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Grid from '@mui/material/Grid2';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { isOptionEqualToValue } from 'feature/dispatching/utils';
import { initialProductState } from './initialState';
import {
  useGetDestinationsQuery,
  useGetRecipesQuery,
  useLazyGetAdditivesQuery,
} from '../../loadsEndpoints';
import ProductForm from './ProductForm';
import LoadingTextField from './LoadingTextField';

const renderValue = (tags, getItemProps) =>
  tags.map((item, index) => (
    <Chip
      label={item.name}
      sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
      variant="outlined"
      {...getItemProps({ index })}
    />
  ));

const isPropNil = R.curryN(2, R.compose(R.isNil, R.prop));

const accordionStyles = {
  boxShadow: 'none',
  fontSize: '1.25em',
  m: 2,
};
const accordionDetailsStyles = {
  backgroundColor: 'surface.white',
};

const HouseRecipeForm = ({ loadState, setLoadState, compartment }) => {
  const { location } = loadState;
  const { compartmentId, recipe, products, additives, destination } = compartment;

  const [destinationAutocomplete, setDestinationAutocomplete] = useState('name');
  const compartmentLens = R.lensPath(['compartments', compartmentId]);
  const recipeLens = R.compose(compartmentLens, R.lensProp('recipe'));
  const productsLens = R.compose(compartmentLens, R.lensProp('products'));
  const destinationLens = R.compose(compartmentLens, R.lensProp('destination'));
  const additivesLens = R.compose(compartmentLens, R.lensProp('additives'));
  const volumeLens = R.compose(compartmentLens, R.lensProp('volume'));

  const {
    data: recipesQuery = [],
    isFetching: isFetchingRecipes,
    isSuccess: isRecipesSuccess,
  } = useGetRecipesQuery({ locationId: R.prop('locationId', location) });

  const [
    triggerGetAdditives,
    { data: additivesQuery = [], isFetching: isFetchingAdditives, isSuccess: isAdditivesSuccess },
  ] = useLazyGetAdditivesQuery();

  const {
    data: destinationsQuery = [],
    isFetching: isFetchingDestinations,
    isSuccess: isDestinationsSuccess,
  } = useGetDestinationsQuery(
    {
      loadingControlIds: R.map(R.path(['loadingControl', 'loadingControlId']), R.values(products)),
      locationId: R.prop('locationId')(location),
    },
    {
      skip: [
        R.isEmpty(products),
        R.any(isPropNil('supplier'), R.values(products)),
        R.any(
          R.ifElse(R.path(['supplier', 'hasLoadingControls']), isPropNil('loadingControl'), R.F),
          R.values(products)
        ),
      ].includes(true),
    }
  );

  const handleRecipeChange = (_, value) => {
    setLoadState(
      R.compose(
        R.set(additivesLens, []),
        R.over(productsLens, R.map(R.assoc('supplier', null))),
        R.over(productsLens, R.map(R.assoc('loadingControl', null))),
        R.set(productsLens, R.indexBy(R.prop('productId'), value.products)),
        R.set(recipeLens, value),
        R.set(volumeLens, ''),
        R.set(destinationLens, null)
      )
    );
  };

  const handleDestinationChange = (_, value) => {
    setLoadState(R.set(destinationLens, value));
  };

  const handleAdditivesChange = (_, value) => {
    setLoadState(R.set(additivesLens, value));
  };

  useEffect(() => {
    if (R.isEmpty(products) && isRecipesSuccess && R.not(R.isNil(recipe))) {
      const indexedProducts = R.compose(
        R.indexBy(R.prop('productId')),
        R.map(R.mergeRight(initialProductState))
      )(recipe.products);

      setLoadState(R.set(productsLens, indexedProducts));
    }
  }, [isRecipesSuccess, products, productsLens, recipe, setLoadState]);

  useEffect(() => {
    if (!recipe) return;

    const thisRecipe = recipesQuery.find(
      r => r.recipeId === recipe.recipeId || r.productGradeCode === recipe.recipeId
    );
    if (thisRecipe) {
      setLoadState(R.set(recipeLens, thisRecipe));
      if (
        !R.isNil(location) &&
        !R.isEmpty(products) &&
        !R.any(isPropNil('supplier'), R.values(products))
      ) {
        triggerGetAdditives({
          locationId: R.prop('locationId', location),
          productGradeCode: thisRecipe.productGradeCode,
          supplierIds: R.map(R.path(['supplier', 'supplierId']), R.values(products)),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecipesSuccess, recipe, products]);

  const handleToggle = (e, selected) => {
    setDestinationAutocomplete(selected);
  };

  return (
    <Stack spacing={2}>
      <Autocomplete
        disableClearable
        disabled={!isRecipesSuccess}
        getOptionLabel={R.prop('name')}
        isOptionEqualToValue={isOptionEqualToValue('recipeId')}
        loading={isFetchingRecipes}
        onChange={handleRecipeChange}
        options={recipesQuery}
        renderInput={params => (
          <LoadingTextField label="Recipe" loading={isFetchingRecipes} params={params} />
        )}
        value={recipe}
      />
      {Object.values(products).map(p => (
        <ProductForm
          compartment={compartment}
          key={p.productGradeCode}
          loadState={loadState}
          product={p}
          setLoadState={setLoadState}
        />
      ))}
      <Autocomplete
        disableClearable
        disabled={!isAdditivesSuccess}
        getOptionLabel={R.prop('name')}
        isOptionEqualToValue={isOptionEqualToValue('additiveId')}
        loading={isFetchingAdditives}
        multiple
        onChange={handleAdditivesChange}
        options={additivesQuery}
        renderInput={params => (
          <LoadingTextField label="Additives" loading={isFetchingAdditives} params={params} />
        )}
        renderValue={renderValue}
        value={additives}
      />
      <Typography label="Destination Information" sx={{ mt: 2, mx: 2 }}>
        Destination Information
      </Typography>
      <ToggleButtonGroup exclusive onChange={handleToggle} value={destinationAutocomplete}>
        <ToggleButton value="name">Name</ToggleButton>
        <ToggleButton value="address">Address</ToggleButton>
      </ToggleButtonGroup>
      {destinationAutocomplete === 'address' ? (
        <Autocomplete
          disableClearable
          disabled={!isDestinationsSuccess}
          getOptionLabel={({ city, state, street, zipCode }) =>
            `${street} ${city}, ${state} ${zipCode} `
          }
          isOptionEqualToValue={isOptionEqualToValue('destinationId')}
          loading={isFetchingDestinations}
          onChange={handleDestinationChange}
          options={destinationsQuery}
          renderInput={params => (
            <LoadingTextField label="ADDRESS" loading={isFetchingDestinations} params={params} />
          )}
          value={destination}
        />
      ) : (
        <Autocomplete
          disableClearable
          disabled={!isDestinationsSuccess}
          getOptionLabel={({ destinationId, name }) => `${destinationId}: ${name}`}
          isOptionEqualToValue={isOptionEqualToValue('destinationId')}
          loading={isFetchingDestinations}
          onChange={handleDestinationChange}
          options={destinationsQuery}
          renderInput={params => (
            <LoadingTextField label="NAME" loading={isFetchingDestinations} params={params} />
          )}
          value={destination}
        />
      )}
      <Accordion sx={accordionStyles}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} label="Destination" sx={{ p: 1 }}>
          Destination
        </AccordionSummary>
        <AccordionDetails sx={accordionDetailsStyles}>
          <Grid columnSpacing={{ md: 3, sm: 2, xs: 1 }} container rowSpacing={1}>
            <Grid gridColumn="span 4" size={{ xs: 6 }}>
              <Typography gutterBottom item variant="caption">
                Destination ID{' '}
              </Typography>
              <Typography sx={{ mb: 2 }} variant="body1bold">
                {destination ? destination?.destinationId : null}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography gutterBottom item variant="caption">
                Destination Name{' '}
              </Typography>
              <Typography item sx={{ mb: 2 }} variant="body1bold">
                {destination ? destination?.name : null}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography gutterBottom variant="caption">
                Address{' '}
              </Typography>
              <Typography sx={{ mb: 2 }} variant="body1bold">
                {destination
                  ? `${destination?.street} ${destination?.city}  ${destination?.state}  
                    ${destination?.zipCode ? destination.zipCode : ''}`
                  : null}
              </Typography>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};

HouseRecipeForm.propTypes = {
  compartment: PropTypes.shape({
    additives: PropTypes.arrayOf(PropTypes.shape({})),
    compartmentId: PropTypes.number,
    destination: PropTypes.arrayOf(PropTypes.shape({})),
    products: PropTypes.shape({}),
    recipe: PropTypes.shape({
      products: PropTypes.arrayOf(PropTypes.shape({})),
      recipeId: PropTypes.string,
    }),
  }).isRequired,
  loadState: PropTypes.shape({
    compartments: PropTypes.shape({}),
    location: PropTypes.shape({}),
  }).isRequired,
  setLoadState: PropTypes.func.isRequired,
};

export default HouseRecipeForm;
